import React, { useMemo } from 'react';
import { Form, Input, Row, Col, Collapse, Tag } from 'antd';
import PropTypes from 'prop-types';

/**
 * Render các trường động (columns) theo Thể loại sự kiện đang chọn.
 * Mỗi type có cột `columns` (JSON mảng tên trường). Giá trị các trường này
 * được lưu vào `metadata` (thông qua form field lồng: metaCols[<tên cột>]).
 *
 * Hiển thị trong panel thu gọn (giống CMS cũ chỉ là một khối phụ dưới ô Thể loại):
 * form sự kiện đã rất dài, xổ sẵn 5-10 ô trống làm loãng các trường chính.
 * Panel đóng thì Form.Item bên trong chưa mount, nhưng giá trị đã setFieldsValue
 * vẫn nằm trong form store nên lưu không mất dữ liệu metadata cũ.
 *
 * Component phải nằm trong <Form> để dùng được Form context (useWatch/Form.Item).
 */
const parseColumns = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const DynamicTypeColumns = ({ typesList = [] }) => {
  const typeId = Form.useWatch('type_id');
  const metaCols = Form.useWatch('metaCols');

  const columns = useMemo(() => {
    if (!typeId) return [];
    const type = typesList.find((t) => String(t.id) === String(typeId));
    return parseColumns(type?.columns);
  }, [typeId, typesList]);

  const filledCount = useMemo(
    () => columns.filter((col) => String(metaCols?.[col] ?? '').trim() !== '').length,
    [columns, metaCols]
  );

  if (!columns.length) return null;

  return (
    <Collapse
      ghost
      expandIconPosition="end"
      className="mt-4 border border-gray-200 rounded-xl bg-gray-50"
      items={[
        {
          key: 'metaCols',
          label: (
            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">
              Thông tin theo thể loại
              <Tag className="ml-2 normal-case tracking-normal font-normal" color={filledCount ? 'blue' : 'default'}>
                {filledCount ? `${filledCount}/${columns.length} đã nhập` : `${columns.length} trường`}
              </Tag>
            </span>
          ),
          children: (
            <Row gutter={[16, 0]} className="w-full">
              {columns.map((col) => (
                <Col span={24} md={12} key={col}>
                  <Form.Item name={['metaCols', col]} label={col} className="mb-2">
                    <Input placeholder={col} allowClear />
                  </Form.Item>
                </Col>
              ))}
            </Row>
          ),
        },
      ]}
    />
  );
};

DynamicTypeColumns.propTypes = {
  typesList: PropTypes.array,
};

export default DynamicTypeColumns;
